import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import type {
  ProjectMember,
  AddProjectMembersBatchInput,
  AddProjectMembersBatchResponse,
} from "@/types/project.types";
import type { AppSettings } from "@/types/app-settings.types";
import { toast } from "sonner";
import { apiService, appSettingsService } from "@/lib/api-service";
import { useMemo, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";

interface ProjectAddMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  employees: Array<{
    id: string;
    name: string;
    email: string;
    department: string;
  }>;
  existingMembers?: ProjectMember[];
}

interface SelectedMember {
  id: string;
  email: string;
  name: string;
  department: string;
}

export function ProjectAddMemberDialog({
  open,
  onOpenChange,
  projectId,
  employees,
  existingMembers = [],
}: ProjectAddMemberDialogProps) {
  const queryClient = useQueryClient();

  const employeeSelectInputRef = useRef<HTMLButtonElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<"all" | string>(
    "all"
  );
  const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>([]);

  // Fetch departments from app settings
  const { data: appSettings } = useQuery<AppSettings>({
    queryKey: ["app-settings"],
    queryFn: () => appSettingsService.getAll(),
  });

  function onSubmit() {
    if (selectedMembers.length === 0) {
      toast.error("Please select at least one member to add");
      return;
    }

    setIsSubmitting(true);

    // Add all selected members using the batch endpoint
    const batchData: AddProjectMembersBatchInput = {
      employeeIds: selectedMembers.map((member) => member.id),
    };

    toast.promise(
      apiService.post<AddProjectMembersBatchResponse>(
        `/projects/${projectId}/members/batch`,
        batchData
      ),
      {
        loading: `Adding ${selectedMembers.length} member(s)...`,
        success: (response) => {
          queryClient.invalidateQueries({
            queryKey: ["project-members", projectId],
          });
          queryClient.invalidateQueries({ queryKey: ["project", projectId] });
          queryClient.invalidateQueries({
            queryKey: ["project-revision-history", projectId],
          });

          handleOpenChange(false);

          return response.message;
        },
        error: "Failed to add some members",
        finally() {
          setIsSubmitting(false);
        },
      }
    );
  }

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      // Reset form when dialog is closed
      setSelectedDepartment("all");
      setSelectedMembers([]);
    }
  };

  const handleAddMember = (id: string) => {
    const employee = availableEmployees.find((emp) => emp.id === id);
    if (
      employee &&
      !selectedMembers.find((member) => member.id === employee.id)
    ) {
      setSelectedMembers((prev) => [
        ...prev,
        {
          id: employee.id,
          email: employee.email,
          name: employee.name,
          department: employee.department,
        },
      ]);

      // Focus the combobox after adding a member for better UX
      if (employeeSelectInputRef.current) {
        employeeSelectInputRef.current.focus();
      }
    }
  };

  const handleRemoveMember = (id: string) => {
    setSelectedMembers((prev) => prev.filter((member) => member.id !== id));
  };

  // Filter out employees who are already project members or already selected
  const existingMemberEmails = existingMembers.map((member) => member.email);
  const selectedMemberEmails = selectedMembers.map((member) => member.email);
  const unavailableEmails = [...existingMemberEmails, ...selectedMemberEmails];

  const availableEmployees = employees.filter(
    (employee) => !unavailableEmails.includes(employee.email)
  );

  const employeeOptions = useMemo(() => {
    let employees = availableEmployees;
    // Filter by selected department if one is selected
    if (selectedDepartment && selectedDepartment !== "all") {
      employees = availableEmployees.filter(
        (employee) => employee.department === selectedDepartment
      );
    }

    return employees
      .map((employee) => ({
        value: employee.id,
        label: `${employee.name}`,
        description: employee.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) as ComboboxOption[];
  }, [selectedDepartment, availableEmployees]);

  const availableDepartments = appSettings?.department_list?.sort((a, b) =>
    a.localeCompare(b)
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Project Members</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="space-y-4"
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Select
                value={selectedDepartment}
                onValueChange={setSelectedDepartment}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All departments" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>

                  <Separator className="!my-1" />
                  {availableDepartments?.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Employee</Label>
              <Combobox
                ref={employeeSelectInputRef}
                options={employeeOptions}
                onValueChange={(value: string) => {
                  if (value) {
                    handleAddMember(value);
                  }
                }}
                placeholder="Select an employee"
              />

              {availableEmployees.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {selectedDepartment
                    ? `No available employees in ${selectedDepartment} department.`
                    : "All employees are already members of this project."}
                </p>
              )}
            </div>

            {selectedMembers.length > 0 && (
              <div className="space-y-2">
                <Separator className="!mt-6 !mb-4" />
                <Label>Selected Members ({selectedMembers.length})</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {selectedMembers.map((member) => (
                    <div
                      key={member.email}
                      className="flex items-center justify-between p-2 border rounded-md bg-muted/50"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {member.name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {member.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {member.department}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.id)}
                        className="ml-2 h-6 w-6 p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || selectedMembers.length === 0}
            >
              {isSubmitting
                ? `Adding ${selectedMembers.length} member(s)...`
                : `Add ${selectedMembers.length} Member(s)`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
